import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { useViewFeaturesStore } from '@/store/viewFeaturesStore';
import { updateHandlePosition, removeHandlePoint } from '@/utils/viewFeatures';
import { AnalysisTab } from '@/components/home/home';

export interface HandleDragHandlerParams {
	canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
	cameraRef: React.MutableRefObject<THREE.Camera | null>;
	sceneRef: React.MutableRefObject<THREE.Scene | null>;
	controlsRef: React.MutableRefObject<{ controls: OrbitControls } | null>;
	sceneInitialized: boolean;
  tab: AnalysisTab;
}

export const useHandleDragHandler = (params: HandleDragHandlerParams) => {
	const { canvasRef, cameraRef, sceneRef, controlsRef, sceneInitialized, tab } = params;
	const draggedHandleRef = useRef<{
		areaId: string;
		handleIndex: number;
		offset: THREE.Vector3;
	} | null>(null);
	
	const isDraggingRef = useRef(false);
		const justDraggedRef = useRef(false);
	
	// Track the final position to save on mouseup
	const finalPositionRef = useRef<{
		areaId: string;
		handleIndex: number;
		points: THREE.Vector3[];
	} | null>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const scene = sceneRef.current;
		const camera = cameraRef.current;

    if (!canvas || !scene || !camera || tab.id !== "windows-openings") return;


		const raycaster = new THREE.Raycaster();
		const mouse = new THREE.Vector2();

		const handleMouseDown = (event: MouseEvent) => {
			const { selectedPaintedAreaId } = useViewFeaturesStore.getState();
			if (!selectedPaintedAreaId) return;

			// Check if clicking on a handle
			const rect = canvas.getBoundingClientRect();
			mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
			mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

			raycaster.setFromCamera(mouse, camera);

			const handlesGroup = scene.getObjectByName('__paintedAreaHandles');
			if (!handlesGroup) return;

			raycaster.params.Mesh = { threshold: 1 }; // Increase threshold for easier selection
			const intersects = raycaster.intersectObjects(handlesGroup.children, true);

			if (intersects.length > 0) {
				const handle = intersects[0].object;
				// Check both handle and glow
				if (handle.userData.isHandle || handle.userData.isHandleGlow) {
					// Immediately prevent any other handlers from processing this event
					event.preventDefault();
					event.stopPropagation();
					event.stopImmediatePropagation();
					
					const handleIndex = handle.userData.handleIndex;
					const areaId = handle.userData.areaId;
					
					// Handle right-click to remove handle point
					if (event.button === 2) {
						removeHandlePoint(areaId, handleIndex, scene);
						return;
					}
					
					// Left-click to start drag
					if (event.button === 0) {
						if (controlsRef.current?.controls) {
							controlsRef.current.controls.enabled = false;
						}
						
						isDraggingRef.current = true;
						
						draggedHandleRef.current = {
							areaId: areaId,
							handleIndex: handleIndex,
							offset: new THREE.Vector3(),
						};

						const handlePos = handle.position.clone();
						const rayOrigin = raycaster.ray.origin.clone();
						const rayDir = raycaster.ray.direction.clone();
						const distance = rayOrigin.distanceTo(handlePos);
						const pointOnRay = rayOrigin.add(rayDir.multiplyScalar(distance));
						draggedHandleRef.current.offset.subVectors(handlePos, pointOnRay);
					}
				}
			}
		};

		const handleMouseMove = (event: MouseEvent) => {
			if (!draggedHandleRef.current || !isDraggingRef.current) return;
			
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();

			const rect = canvas.getBoundingClientRect();
			mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
			mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

			raycaster.setFromCamera(mouse, camera);

			// Find terrain to raycast against
			const terrainObjects: THREE.Object3D[] = [];
			scene.traverse((child) => {
				if (child.userData.category === 'terrain') {
					terrainObjects.push(child);
				}
			});

			if (terrainObjects.length > 0) {
				const intersects = raycaster.intersectObjects(terrainObjects, true);
				if (intersects.length > 0) {
					const newPosition = intersects[0].point.clone();
					
					const updatedPoints = updateHandlePosition(
						draggedHandleRef.current.areaId,
						draggedHandleRef.current.handleIndex,
						newPosition,
						scene,
						false // Don't save to store during drag
					);
					
					if (updatedPoints) {
						finalPositionRef.current = {
							areaId: draggedHandleRef.current.areaId,
							handleIndex: draggedHandleRef.current.handleIndex,
							points: updatedPoints
						};
					}
				}
			}
		};

		const handleMouseUp = (event: MouseEvent) => {
			if (draggedHandleRef.current || isDraggingRef.current) {
				event.preventDefault();
				event.stopPropagation();
				event.stopImmediatePropagation();
				
				if (draggedHandleRef.current && finalPositionRef.current) {
					const { paintedAreas } = useViewFeaturesStore.getState();
					const area = paintedAreas.find(a => a.id === finalPositionRef.current!.areaId);
					
					if (area) {
						const lastPosition = finalPositionRef.current.points[draggedHandleRef.current.handleIndex];
						
						updateHandlePosition(
							draggedHandleRef.current.areaId,
							draggedHandleRef.current.handleIndex,
							lastPosition,
							scene,
							true // Save to store and regenerate handles
						);
					}
					
					finalPositionRef.current = null;
				}
				
				// Re-enable OrbitControls after drag
				if (controlsRef.current?.controls) {
					controlsRef.current.controls.enabled = true;
				}
				
				// Mark that we just finished a drag to prevent click event from deselecting
				justDraggedRef.current = true;
				
				draggedHandleRef.current = null;
				isDraggingRef.current = false;
			}
		};
		
		const handleClick = (event: MouseEvent) => {
			// If we just finished a drag, prevent the click event from propagating
			if (justDraggedRef.current) {
				event.preventDefault();
				event.stopPropagation();
				event.stopImmediatePropagation();
				justDraggedRef.current = false;
				return;
			}
		};

		canvas.addEventListener('mousedown', handleMouseDown, true);
		canvas.addEventListener('mousemove', handleMouseMove, true);
		canvas.addEventListener('mouseup', handleMouseUp, true);
		canvas.addEventListener('click', handleClick, true);

		return () => {
			canvas.removeEventListener('mousedown', handleMouseDown, true);
			canvas.removeEventListener('mousemove', handleMouseMove, true);
			canvas.removeEventListener('mouseup', handleMouseUp, true);
			canvas.removeEventListener('click', handleClick, true);
		};
	}, [canvasRef, cameraRef, sceneRef, controlsRef, sceneInitialized, tab]);
};
